import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Clock, CheckCircle, XCircle, LogOut, Mail } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { supabaseClient } from '../config/supabase'
import toast from 'react-hot-toast'
import type { AlumniProfile } from '../types'

const PendingApprovalPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [profile, setProfile] = useState<AlumniProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [emailConfirmed, setEmailConfirmed] = useState<boolean | null>(null)
  const [resending, setResending] = useState(false)
  const [checking, setChecking] = useState(false)

  useEffect(() => {
    fetchProfileStatus(false)
  }, [user])

  const fetchProfileStatus = async (showToast = true) => {
    if (!user) {
      navigate('/alumni-login')
      return
    }

    try {
      // Check email confirmation status
      const { data: { user: supaUser } } = await supabaseClient.auth.getUser()
      setEmailConfirmed(!!supaUser?.email_confirmed_at)

      const { data, error } = await supabaseClient
        .from('alumni_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error) throw error

      setProfile(data)
      
      // Redirect if approved
      if (data.approval_status === 'approved') {
        navigate('/dashboard')
      } else if (data.approval_status === 'pending' && showToast) {
        toast('Your account is still awaiting admin approval.', { icon: '⏳' })
      }
    } catch (err: any) {
      console.error('Error fetching profile:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    if (!user?.email) return
    setResending(true)
    try {
      const { error } = await supabaseClient.auth.resend({
        type: 'signup',
        email: user.email,
      })
      if (error) throw error
      toast.success('Verification email resent! Check your inbox.')
    } catch (err: any) {
      toast.error('Failed to resend: ' + (err.message || 'Unknown error'))
    } finally {
      setResending(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/alumni-login')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Show email verification required
  if (emailConfirmed === false) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-2">
              <Link to="/" className="flex items-center">
                <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
              </Link>
              <nav className="hidden md:flex space-x-8">
                <Link to="/news" className="text-gray-600 hover:text-gray-900">News & Updates</Link>
                <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
                <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
                <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
              </nav>
              <button onClick={handleLogout} className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="bg-white border border-yellow-200 rounded-2xl shadow-lg p-8">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Mail className="w-10 h-10 text-yellow-600" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
                <p className="text-gray-600">
                  Please confirm your email address before your account can be reviewed.
                </p>
              </div>
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    We sent a verification link to <span className="font-semibold">{user?.email}</span>.
                    Check your inbox (and spam folder) and click the link to continue.
                  </p>
                </div>
                <button
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <Mail className="w-5 h-5" />
                  <span>{resending ? 'Sending...' : 'Resend Verification Email'}</span>
                </button>
                <button
                  onClick={() => fetchProfileStatus()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>I've verified — refresh status</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show rejection message if profile was rejected
  if (profile?.approval_status === 'rejected') {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-2">
              <Link to="/" className="flex items-center">
                <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
              </Link>
              <nav className="hidden md:flex space-x-8">
                <Link to="/news" className="text-gray-600 hover:text-gray-900">News & Updates</Link>
                <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
                <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
                <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
              </nav>
              <button onClick={handleLogout} className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="bg-white border border-red-200 rounded-2xl shadow-lg p-8">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircle className="w-10 h-10 text-red-600" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Registration Rejected</h1>
                <p className="text-gray-600">
                  Your alumni account registration was not approved by your department admin.
                </p>
              </div>

              {profile.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <h3 className="text-sm font-semibold text-red-900 mb-2">Reason for Rejection:</h3>
                  <p className="text-sm text-red-700">{profile.rejection_reason}</p>
                </div>
              )}

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Your Submitted Information:</h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><span className="font-medium">Name:</span> {profile.first_name} {profile.last_name}</p>
                    <p><span className="font-medium">Email:</span> {user?.email}</p>
                    <p><span className="font-medium">Department:</span> {profile.department}</p>
                    <p><span className="font-medium">Program:</span> {profile.program}</p>
                    {profile.year_graduated && (
                      <p><span className="font-medium">Year Graduated:</span> {profile.year_graduated}</p>
                    )}
                  </div>
                </div>

                <button
                  onClick={handleLogout}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show pending approval message
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: 'url(/uc_nobg.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <Link to="/" className="flex items-center">
              <img src="/trailblaze_logo.png" alt="Trailblaze" className="h-20" />
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/news" className="text-gray-600 hover:text-gray-900">News & Updates</Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900">Features</Link>
              <Link to="/#benefits" className="text-gray-600 hover:text-gray-900">Benefits</Link>
              <Link to="/#about" className="text-gray-600 hover:text-gray-900">About UCLM</Link>
            </nav>
            <button onClick={handleLogout} className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white border border-blue-200 rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center animate-pulse">
                  <Clock className="w-10 h-10 text-blue-600" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Pending Approval</h1>
              <p className="text-gray-600">
                Your alumni account is waiting for approval from your department admin.
              </p>
            </div>

            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-1">What happens next?</h3>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li>Your department admin will review your registration</li>
                  <li>Once approved, you'll gain full access to the platform</li>
                </ul>
              </div>

              <p className="text-xs text-center text-gray-400">Not reviewed in 3–5 business days? Contact your department admin.</p>

              <div className="flex space-x-3">
                <button
                  onClick={async () => {
                    setChecking(true)
                    await fetchProfileStatus(true)
                    setChecking(false)
                  }}
                  disabled={checking}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {checking ? (
                    <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Checking...</span></>
                  ) : (
                    <><CheckCircle className="w-5 h-5" /><span>Check Status</span></>
                  )}
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PendingApprovalPage
